import {useQuery, type QueryKey} from '@github-ui/react-query'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {getQueryClient} from '@github-ui/react-core/query-client'
import type {DiffAnnotation, Thread} from '@github-ui/conversations'
import {useCallback} from 'react'

export function pullRequestMarkersKey(basePath: string): QueryKey {
  return [PageData.markers, basePath]
}

function pullRequestMarkersPayload(basePath: string) {
  const queryKey = pullRequestMarkersKey(basePath)
  return {
    queryKey,
    queryFn: async () => {
      // we're not fetching from an API yet, seeding TSQ with payload data for re-use
      return undefined
    },
  }
}

export type Markers = {
  threads: {[id: number]: Thread}
  annotations: {[id: number]: DiffAnnotation}
}
export function useMarkersData({basePath, initialData}: {basePath: string; initialData?: Markers}) {
  const {queryFn, queryKey} = pullRequestMarkersPayload(basePath)
  return useQuery<Markers | undefined>({
    queryKey,
    queryFn,
    initialData,
    staleTime: Infinity,
  })
}

export function useMarkersDataWithSelectThreadIDs({basePath, threadIDs}: {basePath: string; threadIDs: number[]}) {
  const {queryFn, queryKey} = pullRequestMarkersPayload(basePath)
  return useQuery({
    queryKey,
    queryFn,
    staleTime: Infinity,
    select: useCallback(
      (data: Markers | undefined) => {
        if (!data) return undefined
        const threads = Object.fromEntries(
          Object.entries(data.threads).filter(([id]) => threadIDs.includes(parseInt(id))),
        )
        if (Object.keys(threads).length === 0) return undefined
        return {threads, annotations: {}}
      },
      [threadIDs],
    ),
  })
}

export function getExistingMarkersData({basePath}: {basePath: string}) {
  const {queryKey} = pullRequestMarkersPayload(basePath)
  const queryClient = getQueryClient()

  return queryClient.getQueryData(queryKey)
}
